/* eslint-disable react/prop-types */
import * as React from 'react';
import { graphql } from 'gatsby';

import { EventPage } from '../../components/EventPage/EventPage';
import { PageLayout } from '../../layouts/PageLayout/PageLayout';
import { themeToVariables } from '../../utils/theme';
import { SEO } from '../../components/SEO/SEO';
import { getEventTags } from '../../utils/event';
import { sortByOrder } from '../../utils/array';

const Event = ({ data, location, pageContext }: any) => {
  const snippet = data.snippet.data;

  const {
    theme,
    title,
    coverImage,
    bgImage,
    bgImageMobileOriginal,
    bgImageOriginal,
    isBgFullSize,
    bgColorOverflow,
    services,
    skills,
    opensourceProjects,
    customTitle,
    body,
    intro,
    rawIntro,
    categories,
    products,
    clients,
    authors,
    randomColorIndex,
    type,
    links,
    cases,
    customCss,
    event: {
      dateStartFull,
      dateStartISO,
      city,
      title: eventTitle,
      url,
      dateStartDay,
      dateStartMonth,
      dateEndISO,
      dateEndDay,
      dateEndMonth,
      signupUrl,
      upcoming,
      passed,
    },
    talk,
    slides,
    posts,
  } = data.event;

  const caseStudy = !body && cases && cases[0] ? cases[0] : null;

  const { colors: themeVariables, meta: themeMeta } = themeToVariables(
    theme,
    bgImage && bgColorOverflow,
    randomColorIndex,
  );

  const productsEntries = products.sort(sortByOrder);

  const sortedOpesourceProjects = opensourceProjects
    .filter(
      (project: any) =>
        !productsEntries.some((product: any) => product.slug === project.slug),
    )
    .sort(sortByOrder);

  const tags = getEventTags({
    categories,
    services,
    skills,
    slice: false,
    type,
  });

  const sortedClients = clients.sort(sortByOrder);

  const moreEvents = [
    ...data.moreEventsAuthor.edges,
    ...data.moreEventsService.edges,
    ...data.nextEvents.edges,
    ...data.firstEvents.edges,
  ].slice(0, 2);

  const bgImages = {
    default: bgImage,
    original: {
      desktop: bgImageOriginal,
      mobile: bgImageMobileOriginal,
    },
  };

  return (
    <PageLayout
      themeVariables={themeVariables}
      themeMeta={themeMeta}
      title={customTitle || `${title} ${snippet.pageTitle}`}
      description={rawIntro}
      location={location}
      cover={coverImage?.publicURL || bgImage?.publicURL}
      socialCard={pageContext.socialCard}
      withBgImage={Boolean(bgImage)}
      page="event"
      dateISO={dateStartISO}
      endDateISO={dateEndISO}
      authors={authors}
      address={city}
      eventType={type}
      url={
        url ||
        talk?.video_url ||
        talk?.live_url ||
        (talk?.audio_urls && talk?.audio_urls.length > 0
          ? talk?.audio_urls[0]
          : null)
      }
      tags={tags}
      showUpcomingEvent={!upcoming}
      article
      isBgFullSize={isBgFullSize}
    >
      <EventPage
        pageTitle={title}
        intro={intro}
        rawIntro={rawIntro}
        dateStart={dateStartFull}
        dateStartISO={dateStartISO}
        dateStartDay={dateStartDay}
        dateStartMonth={dateStartMonth}
        upcoming={upcoming}
        dateEndDay={dateEndDay}
        dateEndMonth={dateEndMonth}
        passed={passed}
        eventType={type}
        city={city}
        eventTitle={eventTitle}
        url={url}
        coverImage={coverImage}
        bgImages={bgImages}
        isBgFullSize={isBgFullSize}
        bgColorOverflow={bgColorOverflow}
        tags={tags}
        whiteBg={themeMeta.whiteBg}
        clients={sortedClients}
        opensourceProjects={sortedOpesourceProjects}
        moreEvents={moreEvents}
        snippet={snippet}
        products={productsEntries}
        authors={authors}
        signupUrl={signupUrl}
        links={links}
        audios={talk?.audio_urls}
        video={talk?.video_url}
        live={talk?.live_url}
        slides={slides?.slides_url}
        posts={posts}
        cases={cases}
        caseStudyPreview={body || caseStudy?.bodyPreview}
        customCss={customCss || caseStudy?.customCss}
        caseStudySlug={caseStudy?.slug}
      />
    </PageLayout>
  );
};

export default Event;

export const Head = ({ data, location, pageContext }: any) => {
  const snippet = data.snippet.data;
  const {
    title,
    customTitle,
    industries,
    services,
    skills,
    coverImage,
    bgImage,
    authors,
    event,
    rawIntro,
    type,
  } = data.event;

  const tags = getEventTags({
    industries,
    services,
    skills,
    slice: false,
    withEntityType: true,
    type,
  });

  return (
    <SEO
      title={customTitle || `${title} ${snippet.pageTitle}`}
      description={rawIntro}
      pathname={location.pathname}
      article
      dateISO={event.dateStartISO}
      authors={authors}
      tags={tags}
      cover={coverImage?.publicURL || bgImage?.publicURL}
      socialCard={pageContext.socialCard}
    />
  );
};

export const query = graphql`
  query EventQuery(
    $slug: String!
    $authors: [String!]
    $services: [String!]
    $dateStart: Date
    $title: String!
  ) {
    snippet(slug: { eq: "event" }) {
      id
      data
    }
    event(slug: { eq: $slug }) {
      ...eventFancyListFields
      body
      intro
      rawIntro
      opensourceProjects {
        ...opensourceProjectFancyListFields
      }
      products {
        ...productFancyListFields
      }
      clients {
        ...clientFancyListFields
        order
      }
      posts {
        ...blogPostFancyListFields
      }
      authors {
        ...authorFields
      }
      services {
        slug
        title
        shortTitle
        description
      }
      event {
        dateStartFull: date_start(formatString: "MMMM D, YYYY")
        dateEndISO: date_end
        url
        signupUrl: signup_url
      }
      links {
        title
        url
      }
      cases {
        ...blogPostFancyListFields
        bodyPreview
        customCss
      }
    }
    moreEventsAuthor: allEvent(
      sort: { event: { date_start: DESC } }
      limit: 2
      filter: {
        slug: { ne: $slug }
        title: { ne: $title }
        rawAuthors: { in: $authors }
      }
    ) {
      edges {
        node {
          ...eventFancyListFields
        }
      }
    }
    moreEventsService: allEvent(
      sort: { event: { date_start: DESC } }
      limit: 2
      filter: {
        slug: { ne: $slug }
        title: { ne: $title }
        rawServices: { in: $services }
      }
    ) {
      edges {
        node {
          ...eventFancyListFields
        }
      }
    }
    nextEvents: allEvent(
      sort: { event: { date_start: DESC } }
      limit: 2
      filter: {
        slug: { ne: $slug }
        title: { ne: $title }
        event: { date_start: { gte: $dateStart } }
      }
    ) {
      edges {
        node {
          ...eventFancyListFields
        }
      }
    }
    firstEvents: allEvent(
      sort: { event: { date_start: DESC } }
      limit: 2
      filter: { slug: { ne: $slug }, title: { ne: $title } }
    ) {
      edges {
        node {
          ...eventFancyListFields
        }
      }
    }
  }
`;
